import { useAuthStore } from "@/stores/authStore";
import supabase from "@/lib/supabase";

export interface ApiRequestOptions extends RequestInit {
  requireAuth?: boolean;
  skipTokenRefresh?: boolean; // Skip refresh check for specific requests
}

class ApiClient {
  private baseUrl;
  private isRedirecting = false;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor() {
    this.baseUrl = import.meta.env.PROD
      ? import.meta.env.VITE_PROD_API_BASE_URL
      : `${window.location.origin}/api`;
  }

  /**
   * Check if token is expired or will expire soon (within 5 minutes)
   */
  private isTokenExpiringSoon(expiresAt: number): boolean {
    const now = Math.floor(Date.now() / 1000);
    const fiveMinutes = 5 * 60;
    return expiresAt - now < fiveMinutes;
  }

  /**
   * Attempt to refresh the session token
   */
  private async refreshToken(): Promise<boolean> {
    // If already refreshing, wait for that promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        console.log("Attempting to refresh token...");
        const { data, error } = await supabase.auth.refreshSession();

        if (error || !data.session) {
          console.error("Token refresh failed:", error);
          return false;
        }

        console.log("Token refreshed successfully");
        // Session is automatically updated in auth store via onAuthStateChange
        return true;
      } catch (error) {
        console.error("Token refresh error:", error);
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Get auth headers with automatic token refresh
   */
  private async getAuthHeaders(
    skipTokenRefresh: boolean = false
  ): Promise<Record<string, string>> {
    const { session } = useAuthStore.getState();

    if (!session?.access_token) {
      throw new Error("No authentication token available");
    }

    // Check if token needs refresh (unless explicitly skipped)
    if (!skipTokenRefresh && session.expires_at) {
      if (this.isTokenExpiringSoon(session.expires_at)) {
        console.log("Token expiring soon, refreshing...");
        const refreshed = await this.refreshToken();

        if (refreshed) {
          // Get the new session after refresh
          const { session: newSession } = useAuthStore.getState();
          if (newSession?.access_token) {
            return {
              Authorization: `Bearer ${newSession.access_token}`,
            };
          }
        }
      }
    }

    return {
      Authorization: `Bearer ${session.access_token}`,
    };
  }

  /**
   * Handle authentication errors with token refresh retry
   */
  private async handleAuthError(
    endpoint: string,
    options: ApiRequestOptions
  ): Promise<Response | null> {
    // Don't retry if we've already tried refreshing or if explicitly skipped
    if (options.skipTokenRefresh) {
      return null;
    }

    console.log("Got 401 error, attempting token refresh...");
    const refreshed = await this.refreshToken();

    if (refreshed) {
      // Retry the request with new token
      console.log("Retrying request with refreshed token...");
      return this.makeRequest(endpoint, {
        ...options,
        skipTokenRefresh: true, // Don't retry again
      });
    }

    return null;
  }

  /**
   * Perform sign out and redirect to login
   */
  private async performSignOut(): Promise<void> {
    if (this.isRedirecting) {
      return;
    }

    this.isRedirecting = true;

    try {
      const { signOut, setLoading } = useAuthStore.getState();
      setLoading(true);

      // Wait for sign out to complete
      await signOut();

      // Use window.location for hard redirect to clear all state
      // This is intentional to ensure clean slate
      window.location.href = "/login";
    } catch (error) {
      console.error("Error during sign out:", error);
      // Force redirect even if sign out fails
      window.location.href = "/login";
    }
  }

  /**
   * Make the actual HTTP request
   */
  private async makeRequest(
    endpoint: string,
    options: ApiRequestOptions
  ): Promise<Response> {
    const {
      requireAuth = true,
      skipTokenRefresh,
      headers = {},
      ...fetchOptions
    } = options;
    const url = `${this.baseUrl}${endpoint}`;

    // Prepare headers
    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Add custom headers
    Object.assign(requestHeaders, headers);

    // Add auth headers if required
    if (requireAuth) {
      try {
        const authHeaders = await this.getAuthHeaders(skipTokenRefresh);
        Object.assign(requestHeaders, authHeaders);
      } catch (error) {
        // If auth is required but no token available, sign out
        await this.performSignOut();
        throw error;
      }
    }

    return fetch(url, {
      ...fetchOptions,
      headers: requestHeaders,
    });
  }

  private async request<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    let response = await this.makeRequest(endpoint, options);

    // Handle authentication errors
    if (response.status === 401 && options.requireAuth !== false) {
      // Try to refresh token and retry
      const retryResponse = await this.handleAuthError(endpoint, options);

      if (retryResponse) {
        response = retryResponse;
      } else {
        // Token refresh failed, sign out
        await this.performSignOut();
        throw new Error("Authentication required");
      }
    }

    // Handle service unavailable (database connection issues)
    if (response.status === 503) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Service unavailable:", errorData.error);
      throw new Error(
        errorData.error || "Service temporarily unavailable. Please try again."
      );
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    // Handle empty responses
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }

    return response.text() as unknown as T;
  }

  // HTTP methods
  async get<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  // Public endpoints (no auth required)
  async publicGet<T>(
    endpoint: string,
    options: Omit<ApiRequestOptions, "requireAuth"> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      requireAuth: false,
      method: "GET",
    });
  }

  async publicPost<T>(
    endpoint: string,
    data?: any,
    options: Omit<ApiRequestOptions, "requireAuth"> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      requireAuth: false,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const apiClient = new ApiClient();
export default apiClient;
